import { isInteractionNoteSource } from "../../FileViewer/services/interactionNoteSource";
import { fetchInteractionByBlobId } from "../../FpPages/RMS/Dashboard/api";
import { BASE_API_URL, BASE_API_URL_V2 } from "@/constants";
import type { FileViewerState } from "@/redux/fileViewerSlice";
import fpFetch from "@/services/fpFetch";

const downloadInteractionPdf = async (
  fileId: string | null,
  selectedId?: string | null
): Promise<boolean> => {
  let interactionId = selectedId;
  if (!interactionId) {
    if (!fileId) return false;
    // Association access is independent of access to the requested file.
    // A raw fallback still has to pass the blob endpoint's authorization.
    const interactionData = await fetchInteractionByBlobId(fileId).catch(
      () => null
    );
    if (!interactionData || !isInteractionNoteSource(interactionData, fileId))
      return false;
    interactionId = interactionData.id;
  }
  // Download the PDF
  const pdfRes = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/interactions/${interactionId}/pdf`
  );
  if (!pdfRes.ok) throw new Error("Failed to export interaction PDF");
  const disposition = pdfRes.headers.get("Content-Disposition") || "";
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
  const filename = filenameMatch?.[1] || "interaction.pdf";
  const blob = await pdfRes.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return true;
};

export type DownloadableFile = Pick<
  FileViewerState,
  "fileId" | "fileType" | "fileName" | "downloadUrl"
> & { interactionId?: string | null };

/** Save the file the viewer is showing. Takes the viewer's state rather than
 *  reading the store, so the caller and the download can't disagree about which
 *  file is open. */
export const downloadFileViewerDataFile = async (
  fileViewerData: DownloadableFile
) => {
  const fileId = fileViewerData.fileId;
  const fileType = fileViewerData.fileType;
  const stateFileName = fileViewerData.fileName;

  if (
    fileType === "finpilot-interaction" &&
    (fileId || fileViewerData.interactionId)
  ) {
    if (await downloadInteractionPdf(fileId, fileViewerData.interactionId))
      return;
    // An attachment association is not this file's note identity. Its own
    // original bytes follow the same authorized download path as any file.
  }

  // `raw=true` on the blob route, and the chat-output route's plain (non-preview)
  // URL, mean the same thing: hand back the file as it was stored, not the
  // rendered preview. A .pptx saved as its PDF render doesn't open.
  //
  // A blob id wins over `downloadUrl` for the same reason it does in the
  // loader: the viewer's state is merged, so a chat output opened earlier can
  // leave its URL behind and would otherwise download the wrong file.
  const fileUrl =
    fileId || !fileViewerData.downloadUrl
      ? `${BASE_API_URL}/userData/fp0065?fileId=${fileId}&v=cacheBust8&raw=true`
      : fileViewerData.downloadUrl;
  const response = await fpFetch(fileUrl);
  if (!response.ok) throw new Error("Failed to download file");

  // Resolve filename: prefer Redux state, fall back to Content-Disposition header
  const resolveFileName = (): string => {
    if (stateFileName) return stateFileName;
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^"]+)"?/i);
    if (match?.[1]) return match[1];
    return "";
  };

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = resolveFileName();
  a.click();
  URL.revokeObjectURL(url);
};
